import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API } from "../../lib/api";
import { RESOURCE_LIST } from "../../data/resources";

const fmtBytes = (n) => {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

export const LeadMagnetsAdmin = ({ adminKey, onUnauthorized }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [slug, setSlug] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/lead-magnets", {
        headers: { "X-TAG-Admin-Key": adminKey },
        validateStatus: () => true,
      });
      if (res.status === 401) { onUnauthorized(); return; }
      setItems(res.data?.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const applyPreset = (preset) => {
    if (!preset) return;
    setSlug(preset.slug);
    setTitle(preset.name);
    setDescription(preset.blurb);
  };

  const onUpload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Pick a PDF first.");
    if (!slug.trim() || !title.trim()) return toast.error("Slug and title required.");
    if (file.size > 25 * 1024 * 1024) return toast.error("Max 25 MB per PDF.");
    setUploading(true);
    const fd = new FormData();
    fd.append("slug", slug.trim());
    fd.append("title", title.trim());
    fd.append("description", description);
    fd.append("file", file);
    try {
      const res = await fetch(`${API}/admin/lead-magnets/upload`, {
        method: "POST",
        headers: { "X-TAG-Admin-Key": adminKey },
        body: fd,
      });
      if (res.status === 401) { onUnauthorized(); return; }
      if (!res.ok) {
        const t = await res.text();
        toast.error(`Upload failed: ${t.slice(0, 120)}`);
        return;
      }
      toast.success("Uploaded.");
      setFile(null);
      setSlug(""); setTitle(""); setDescription("");
      const fileEl = document.getElementById("lm-file-input");
      if (fileEl) fileEl.value = "";
      load();
    } catch (err) {
      toast.error("Upload failed.");
    } finally { setUploading(false); }
  };

  const onDelete = async (s) => {
    if (!confirm(`Delete ${s}?`)) return;
    const res = await api.delete(`/admin/lead-magnets/${s}`, {
      headers: { "X-TAG-Admin-Key": adminKey },
      validateStatus: () => true,
    });
    if (res.status === 200) { toast.success("Deleted."); load(); }
    else if (res.status === 401) { onUnauthorized(); }
    else toast.error("Delete failed.");
  };

  const labelStyle = { display: "block", color: "#888", fontSize: 11, marginBottom: 6, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" };
  const usedSlugs = new Set(items.map((i) => i.slug));

  return (
    <div data-testid="lead-magnets-admin">
      <div className="tag-card" style={{ marginBottom: 24 }}>
        <h2 style={{ color: "#fff", fontSize: 16, fontWeight: 700, margin: "0 0 6px" }}>Upload a PDF lead magnet</h2>
        <p style={{ color: "#888", fontSize: 12, margin: "0 0 16px" }}>
          PDFs are stored on the server and delivered by signed one-time link via email after a visitor requests them at <code style={{ color: "#bbb" }}>/resources/&lt;slug&gt;</code>.
        </p>

        {/* Quick presets */}
        <div style={{ marginBottom: 14 }}>
          <div style={labelStyle}>Quick preset (fills slug + title + description)</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {RESOURCE_LIST.map((p) => (
              <button
                key={p.slug}
                data-testid={`lm-preset-${p.slug}`}
                type="button"
                onClick={() => applyPreset(p)}
                className="tag-pill"
                style={{
                  background: usedSlugs.has(p.slug) ? "#1a2a1a" : "transparent",
                  borderColor: usedSlugs.has(p.slug) ? "#2a4a2a" : "#1a1a1a",
                  color: usedSlugs.has(p.slug) ? "#7acc7a" : "#bbb",
                  fontSize: 11,
                  cursor: "pointer",
                }}
              >
                {usedSlugs.has(p.slug) ? "✓ " : ""}{p.name}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={onUpload}>
          <label style={labelStyle}>Slug</label>
          <input
            data-testid="lm-slug"
            type="text"
            className="tag-input"
            value={slug}
            placeholder="content-engine-blueprint"
            onChange={(e) => setSlug(e.target.value)}
            style={{ marginBottom: 12 }}
          />
          <label style={labelStyle}>Title</label>
          <input
            data-testid="lm-title"
            type="text"
            className="tag-input"
            value={title}
            placeholder="Content Engine Blueprint"
            onChange={(e) => setTitle(e.target.value)}
            style={{ marginBottom: 12 }}
          />
          <label style={labelStyle}>Description (used in email body)</label>
          <textarea
            data-testid="lm-description"
            rows={3}
            className="tag-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{ marginBottom: 12, resize: "vertical", minHeight: 60, fontFamily: "var(--font-body)" }}
          />
          <label style={labelStyle}>PDF file (max 25 MB)</label>
          <input
            id="lm-file-input"
            data-testid="lm-file"
            type="file"
            accept="application/pdf,.pdf"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            style={{ marginBottom: 16, color: "#bbb", fontSize: 13 }}
          />
          <div>
            <button data-testid="lm-upload" type="submit" className="btn-primary" disabled={uploading}>
              {uploading ? "Uploading…" : "Upload PDF"}
            </button>
          </div>
        </form>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
        <h2 style={{ color: "#fff", fontSize: 16, fontWeight: 700, margin: 0 }}>
          Uploaded magnets{items.length > 0 ? ` (${items.length}/6)` : ""}
        </h2>
        <button
          data-testid="lm-refresh"
          type="button"
          onClick={load}
          style={{ background: "transparent", border: "none", color: "#777", fontSize: 12, cursor: "pointer" }}
        >↻ Refresh</button>
      </div>

      {loading && <div className="tag-card" style={{ textAlign: "center", color: "#777" }}>Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="tag-card" style={{ textAlign: "center", color: "#bbb" }}>
          No PDFs uploaded yet. Use the form above to upload your first lead magnet.
        </div>
      )}

      {!loading && items.map((it) => (
        <div
          key={it.slug}
          data-testid={`lm-item-${it.slug}`}
          className="tag-card"
          style={{ marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}
        >
          <div style={{ flex: "1 1 320px", minWidth: 280 }}>
            <div style={{ color: "#fff", fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{it.title}</div>
            <div style={{ color: "#888", fontSize: 11, marginBottom: 6 }}>
              /resources/{it.slug} · {fmtBytes(it.size_bytes)} · uploaded {fmtDate(it.uploaded_at)}
            </div>
            <div style={{ color: "#aaa", fontSize: 12, lineHeight: 1.5 }}>{it.description}</div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <a
              data-testid={`lm-view-${it.slug}`}
              href={`/resources/${it.slug}`}
              target="_blank"
              rel="noreferrer"
              className="btn-ghost"
              style={{ fontSize: 12 }}
            >View page</a>
            <button
              data-testid={`lm-delete-${it.slug}`}
              type="button"
              onClick={() => onDelete(it.slug)}
              className="btn-ghost"
              style={{ color: "#FF6575", fontSize: 12 }}
            >Delete</button>
          </div>
        </div>
      ))}
    </div>
  );
};
